import React from 'react';

export type UseOnScreenHook = (ref: React.RefObject<HTMLElement>) => boolean;

/**
 * Checks if element is visible on screen
 * Usage:
 *
 * @example
 * const elementRef = React.useRef<HTMLDivElement>(null);
 * const isVisible = useOnScreen(elementRef);
 *
 * return <div ref={elementRef}>{isVisible}</div>
 * @returns
 */
const useOnScreen: UseOnScreenHook = (
  ref: React.RefObject<HTMLElement>,
): boolean => {
  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const [isOnScreen, setIsOnScreen] = React.useState(false);

  // Create observer from IntersectionObserver API
  React.useEffect(() => {
    observerRef.current = new IntersectionObserver(([entry]) =>
      setIsOnScreen(entry.isIntersecting),
    );
  }, []);

  React.useEffect(() => {
    if (ref.current && observerRef.current) {
      observerRef.current?.observe(ref.current);
    }
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [ref]);

  return isOnScreen;
};

export default useOnScreen;
